import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { toMutation, toQuery, nodeWithArgs, node } from 'common/util/queryHelper';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import lodash from 'lodash';

import DoAExport from 'pages/delegationOfAuthority/containers/DoAExport';
import DoAExportV2 from 'pages/delegationOfAuthority/v2/containers/DoAExportV2';
import { getTimezoneOffset } from 'common/selectors';
import {
    delegationsList,
} from 'pages/delegationOfAuthority/selectors';
import { isCentralized } from 'pages/delegationOfAuthority/selectors';
import { doaDataSelector } from '../selectors/studyAuditLog';
import DelegationAuditLog from '../util/DelegationAuditLog';
import DelegationSummary from '../util/DelegationSummary';
import Export from '../util/Export';
import {getDatasourceOverrides} from 'institutionalAdmin/actions/institutions';

export const WRITE_DELEGATION_AUDIT_LOG_DATA_V2 = 'WRITE_DELEGATION_AUDIT_LOG_DATA_V2';

export function retrieveStudyAuditLogData(binderId, studyId, institutionId) {
    return async (dispatch, getState) => {
        const storeState = getState();
        institutionId = institutionId || lodash.get(storeState, 'me.institution.id');
        const query = nodeWithArgs('retrieveStudyAuditLogDataV2', {
            institutionId,
            binderId,
            studyId,
        }, [
            node('auditLogEntries', [
                'id',
                'actionUserDisplayName',
                'actionType',
                'assetId',
                'value',
                'notes',
                'createdAt'
            ]),
            node('auditLogUsers', [
                'id',
                'displayName',
            ]),
        ]);

        const response = await queryGraphQL(toQuery(query));
        if (response.errors) {
            return dispatch(addApplicationMessage('error', 'An error occurred when loading the DOA audit log.'));
        }
        const auditLogData = response.data.retrieveStudyAuditLogDataV2;
        if (auditLogData.length === 0) {
            return dispatch(addApplicationMessage('warning', 'An error occurred when loading the DOA audit log.'));
        }

        dispatch(retrieveStudyAuditLogDataAction(auditLogData));
    };
}

export function retrieveStudyAuditLogDataAction(auditLogData) {
    return {
        type: WRITE_DELEGATION_AUDIT_LOG_DATA_V2,
        payload: auditLogData,
    };
}

export function generateExport(institutionId, binderId) {
    return async dispatch => {
        const query = nodeWithArgs('exportDoALogV2', {
            institutionId,
            binderId,
        });

        const {
            errors,
        } = await mutateGraphQL(toMutation(query));

        if (errors) {
            return dispatch(addApplicationMessage('error', 'An error occurred while generating the export'));
        }

        return dispatch(loadAuditLogDataAndGenerateExport(institutionId, binderId));
    };
}

function loadAuditLogDataAndGenerateExport(institutionId, binderId, studyId) {
    return async (dispatch, getState) => {
        const query = nodeWithArgs('retrieveStudyAuditLogDataV2', {
            institutionId,
            binderId,
            studyId,
        }, [
            node('auditLogEntries', [
                'id',
                'actionUserDisplayName',
                'actionType',
                'assetId',
                'value',
                'notes',
                'createdAt'
            ]),
            node('auditLogUsers', [
                'id',
                'displayName',
            ]),
        ]);

        const {
            data: {
                retrieveStudyAuditLogDataV2: auditLogData
            },
            errors,
        } = await queryGraphQL(toQuery(query));

        if (errors) {
            return dispatch(addApplicationMessage('error', 'An error occurred when loading the DOA audit log.'));
        }

        const state = getState();
        const timezoneOffset = getTimezoneOffset(state);
        const DOAData = doaDataSelector(state, auditLogData.auditLogEntries, auditLogData.auditLogUsers);


        const pdfExport = new Export();
        pdfExport.register(new DelegationSummary(DOAData, timezoneOffset));
        pdfExport.register(new DelegationAuditLog(DOAData, timezoneOffset));
        const exportFileName = `${DOAData.studyBinder.title}-delegation-log`;
        pdfExport.generate(exportFileName);

    };
}

export function exportAuditLog(institutionId, binderId) {
    return (dispatch, getState) => {
        const query = nodeWithArgs('exportDoALogV2', {
            institutionId,
            binderId,
        });
        return mutateGraphQL(toMutation(query)).then(results => {
            if (results && results.errors) {
                return dispatch(addApplicationMessage('error', 'Failed to export log'));
            }

            const state = getState();
            const timezoneOffset = getTimezoneOffset(state);
            const doaStudyTasks = lodash.get(state, 'doaStudyTasks', []);
            const doaStudyDelegations = delegationsList(state);
            const binder = lodash.get(state, 'binder', {});
            const testaments = lodash.get(state, 'doaTestaments', []);
            const isCentralizedInstitution = isCentralized(state);

            const exportLog = new DoAExport(timezoneOffset, doaStudyTasks, doaStudyDelegations, binder, testaments, isCentralizedInstitution, state);
            exportLog.exportDOALog();
        });
    };
}

export function retrieveStudyAuditLogDataExport(binderId, studyId, institutionId) {
    return async (dispatch, getState) => {
        const storeState = getState();
        institutionId = institutionId || lodash.get(storeState, 'me.institution.id');
        const query = nodeWithArgs('retrieveStudyAuditLogDataV2', {
            institutionId,
            binderId,
            studyId,
        }, [
            node('auditLogEntries', [
                'id',
                'actionUserDisplayName',
                'actionType',
                'assetId',
                'value',
                'notes',
                'createdAt'
            ]),
            node('auditLogUsers', [
                'id',
                'displayName',
            ]),
        ]);

        const response = await queryGraphQL(toQuery(query));
        if (response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to export log'));
            return Promise.reject('Error occurred');
        }

        const auditLogData = response.data.retrieveStudyAuditLogDataV2;
        if (auditLogData.length === 0) {
            dispatch(addApplicationMessage('warning', 'No audit log data found.'));
            return Promise.resolve();
        }

        dispatch(retrieveStudyAuditLogDataAction(auditLogData));

        return Promise.resolve();
    };
}

export function exportAuditLogV2(institutionId, binderId, studyId) {
    return (dispatch, getState) => {
        dispatch(getDatasourceOverrides(institutionId));
        return dispatch(retrieveStudyAuditLogDataExport(binderId, null, institutionId))
            .then(() => {
                const state = getState();
                const timezoneOffset = getTimezoneOffset(state);
                const doaStudyTasks = lodash.get(state, 'doaStudyTasks', []);
                const doaStudyTasksForExport = lodash.get(state, 'doaStudyTasksForExport', []);
                const doaStudyDelegations = delegationsList(state);
                const binder = lodash.get(state, 'binder', {});
                const testaments = lodash.get(state, 'doaTestaments', []);
                const doaAuditHistory = lodash.get(state, 'doaAuditHistory', {});
                const doaRoles = lodash.get(state, 'doaRoles', {});
                const doaSignatureExpectations = lodash.get(state, 'doaSignatureExpectations', {});
                const me = lodash.get(state, 'me', {});
                const timezone = lodash.get(me, 'institution.properties.Timezone.value', 'US/Eastern');
                const isCentralizedInstitution = isCentralized(state);
                const studyMetadata = lodash.get(state, 'study.metadata', []);
                const selectedDatasourceOverrides = lodash.get(state, 'selectedDatasourceOverrides', []);

                const exportLog = new DoAExportV2(
                    timezoneOffset,
                    timezone,
                    doaStudyTasks,
                    doaStudyTasksForExport,
                    doaStudyDelegations,
                    binder,
                    testaments,
                    doaAuditHistory,
                    doaRoles,
                    doaSignatureExpectations,
                    studyId,
                    isCentralizedInstitution,
                    studyMetadata,
                    selectedDatasourceOverrides
                );
                exportLog.exportDOALog();
            })
            .catch(error => {
                console.error('Failed to export log', error);
                dispatch(addApplicationMessage('error', 'Failed to export log'));
            });
    };
}

